import React, { useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import useAutoCycle from "@/hooks/useAutoCycle";

function StackFace({ item }) {
  return (
    <div className="eh-stack-card-inner">
      <div className="eh-stack-label">
        <span className="eh-stack-icon" aria-hidden="true">
          {item.icon}
        </span>
        <span>{item.label}</span>
      </div>
      <h3>{item.title}</h3>
      <p>{item.description}</p>
      {item.stat != null && (
        <p className="eh-stack-stat">
          <strong>{item.stat}</strong>
          {item.statLabel ? <span> {item.statLabel}</span> : null}
        </p>
      )}
    </div>
  );
}

export default function UseCaseCardStack({
  eyebrow = "Use cases",
  heading,
  items,
}) {
  const [paused, setPaused] = useState(false);
  const rootRef = React.useRef(null);
  const inView = useInView(rootRef, { amount: 0.25 });
  const { index, setIndex, progress } = useAutoCycle(items.length, 5400, paused || !inView);
  const active = items[index] || items[0];
  const next = items[(index + 1) % items.length];
  const after = items[(index + 2) % items.length];

  return (
    <div
      ref={rootRef}
      className="eh-usecase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="eh-usecase-copy">
        <p className="eh-eyebrow">{eyebrow}</p>
        <h2 className="eh-showcase-title">{heading}</h2>
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <h3 className="eh-usecase-sub">{active.title}</h3>
            <p className="eh-showcase-desc">{active.description}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="eh-stack" aria-live="polite">
        {after && after.id !== active.id && (
          <article className="eh-stack-card eh-stack-card--peek eh-stack-card--peek-2">
            <StackFace item={after} />
          </article>
        )}
        {next && next.id !== active.id && (
          <article className="eh-stack-card eh-stack-card--peek eh-stack-card--peek-1">
            <StackFace item={next} />
          </article>
        )}
        <AnimatePresence mode="popLayout">
          <motion.article
            key={active.id}
            className="eh-stack-card is-front"
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1, x: 0, rotate: 0 }}
            exit={{ opacity: 0, y: -64, x: -24, rotate: -3, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <StackFace item={active} />
          </motion.article>
        </AnimatePresence>
      </div>

      <div className="eh-dots" role="tablist" aria-label="Use case slides">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={item.label}
            className={`eh-dot-seg ${i === index ? "is-active" : ""}`}
            onClick={() => setIndex(i)}
          >
            {i === index && (
              <motion.span className="eh-dot-fill" style={{ scaleX: progress }} />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
